// src/pages/Admins/AdminsPage.tsx
import React, {useCallback, useState} from "react";
import {
  Shield,
  ShieldCheck,
  UserPlus,
  Plus,
  Eye,
  Edit,
  Trash2,
  Mail,
  Phone,
  Calendar,
  Crown,
  Settings,
  AlertTriangle,
} from "lucide-react";
import {useTable} from "../../hooks/common/useTable";
import {useToast} from "../../hooks/common/useToast";
import type {UserFilterDto, UserResponseDto} from "../../types/users";
import {
  DataTable,
  LoadingSpinner,
  PageHeader,
  type DataTableColumn,
} from "../../components";
import {
  createCommonBulkActions,
  type DataTableBulkAction,
} from "../../components/common/DataTable";
import {useAdmins} from "../../hooks/useAdmin";
import AdminFormModal from "./components/AdminFormModal";
import AdminDetailModal from "./components/AdminDetailModal";
import StatsCard from "../../components/ui/StatsCard";

const AdminsPage: React.FC = () => {
  const {success} = useToast();
  const [selectedAdmin, setSelectedAdmin] = useState<UserResponseDto | null>(
    null
  );
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Table state management
  const {
    currentPage,
    pageSize,
    handlePageChange,
    handleFilterChange,
    handleSort,
    getQueryParams,
  } = useTable({
    key: "admins",
    defaultPageSize: 25,
  });

  // Build query parameters for API (only admins - ADMIN role)
  const queryParams = {...getQueryParams(), role: "ADMIN"} as UserFilterDto;

  // Use the admins hook with table parameters
  const {
    admins,
    pagination,
    stats,
    isLoading,
    handleUpdate,
    handleDelete,
    handleBulkDelete,
    refetch,
  } = useAdmins(queryParams);

  // Event handlers
  const handleCreateAdmin = useCallback(() => {
    setSelectedAdmin(null);
    setIsEditMode(false);
    setIsFormModalOpen(true);
  }, []);

  const handleEditAdmin = useCallback((admin: UserResponseDto) => {
    setSelectedAdmin(admin);
    setIsEditMode(true);
    setIsFormModalOpen(true);
  }, []);

  const handleViewAdmin = useCallback((admin: UserResponseDto) => {
    setSelectedAdmin(admin);
    setIsDetailModalOpen(true);
  }, []);

  const handleDeleteAdmin = useCallback(
    async (adminId: number) => {
      const result = await handleDelete(adminId);
      if (result) {
        success("Success", "Admin deleted successfully");
        refetch();
      }
    },
    [handleDelete, success, refetch]
  );

  const handleBulkDeleteAdmins = useCallback(
    async (adminIds: number[]) => {
      const result = await handleBulkDelete(adminIds);
      if (result) {
        success("Success", `${adminIds.length} admins deleted successfully`);
        refetch();
      }
    },
    [handleBulkDelete, success, refetch]
  );

  // Table columns definition
  const columns: DataTableColumn<UserResponseDto>[] = [
    {
      key: "name",
      title: "Administrator",
      sortable: true,
      render: (_value: unknown, admin: UserResponseDto) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            {admin.avatar ? (
              <img
                className="h-8 w-8 rounded-full"
                src={admin.avatar}
                alt={admin.name}
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                <Shield className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {admin.name}
              </span>
              <Crown className="h-3 w-3 text-yellow-500" />
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Admin ID: {admin.id}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "email",
      title: "Email",
      sortable: true,
      render: (_value: unknown, admin: UserResponseDto) => (
        <div className="flex items-center space-x-2">
          <Mail className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {admin.email}
          </span>
          {admin.isVerified && (
            <ShieldCheck className="h-4 w-4 text-green-500" />
          )}
        </div>
      ),
    },
    {
      key: "phone",
      title: "Phone",
      render: (_value: unknown, admin: UserResponseDto) =>
        admin.phone ? (
          <div className="flex items-center space-x-2">
            <Phone className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-900 dark:text-gray-100">
              {admin.phone}
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        ),
    },
    {
      key: "isVerified",
      title: "Status",
      sortable: true,
      render: (_value: unknown, admin: UserResponseDto) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            admin.isVerified
              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
          }`}
        >
          <Shield className="w-3 h-3 mr-1" />
          {admin.isVerified ? "Active" : "Pending"}
        </span>
      ),
    },
    {
      key: "lastLogin",
      title: "Last Login",
      sortable: true,
      render: (_value: unknown, admin: UserResponseDto) =>
        admin.lastLogin ? (
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-900 dark:text-gray-100">
              {new Date(admin.lastLogin).toLocaleDateString()}
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">Never</span>
        ),
    },
    {
      key: "createdAt",
      title: "Added",
      sortable: true,
      render: (_value: unknown, admin: UserResponseDto) => (
        <span className="text-sm text-gray-900 dark:text-gray-100">
          {new Date(admin.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ];

  // Row actions
  const actions = [
    {
      key: "view",
      label: "View Details",
      icon: <Eye className="w-4 h-4" />,
      onClick: handleViewAdmin,
    },
    {
      key: "edit",
      label: "Edit",
      icon: <Edit className="w-4 h-4" />,
      onClick: handleEditAdmin,
    },
    {
      key: "settings",
      label: "Admin Settings",
      icon: <Settings className="w-4 h-4" />,
      onClick: (admin: UserResponseDto) => {
        // Handle admin settings/permissions
        console.log("Admin settings for:", admin.id);
      },
    },
    {
      key: "delete",
      label: "Delete",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: (admin: UserResponseDto) => handleDeleteAdmin(admin.id),
      variant: "danger" as const,
    },
  ];

  // Bulk actions
  const commonBulkActions = createCommonBulkActions<UserResponseDto>();
  const bulkActions: DataTableBulkAction<UserResponseDto>[] = [
    commonBulkActions.bulkDelete((admins) =>
      handleBulkDeleteAdmins(admins.map((admin) => admin.id))
    ),
  ];

  // Filter options
  const filters = [
    {
      key: "search",
      label: "Search",
      type: "text" as const,
      placeholder: "Search admins by name or email...",
    },
    {
      key: "isVerified",
      label: "Status",
      type: "select" as const,
      options: [
        {label: "All Admins", value: ""},
        {label: "Active", value: "true"},
        {label: "Pending", value: "false"},
      ],
    },
  ];

  if (isLoading && !admins.length) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Admin Management"
        action={{
          label: "Add Admin",
          onClick: handleCreateAdmin,
          icon: <UserPlus className="w-4 h-4" />,
        }}
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatsCard
            title="Total Admins"
            value={stats.adminUsers}
            icon={<Shield className="w-6 h-6" />}
            color="bg-indigo-100 text-indigo-600"
          />
          <StatsCard
            title="Active Admins"
            value={stats.activeUsers}
            icon={<ShieldCheck className="w-6 h-6" />}
            color="bg-green-100 text-green-600"
          />
          <StatsCard
            title="New This Week"
            value={stats.newUsers}
            icon={<UserPlus className="w-6 h-6" />}
            color="bg-blue-100 text-blue-600"
          />
        </div>
      )}

      {/* Admin Permissions Notice */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Administrator Privileges
            </h3>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              Admins have full system access including user management, property
              oversight, and system configuration. Only grant admin privileges
              to trusted personnel.
            </p>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <DataTable<UserResponseDto>
        data={admins}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize,
          total: pagination?.total || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: true,
          onChange: handlePageChange,
        }}
        sortable
        onSort={(sort) => {
          if (sort) {
            handleSort(sort.field);
          }
        }}
        filters={filters}
        onFilter={(filters) => {
          Object.entries(filters).forEach(([key, value]) => {
            handleFilterChange(key, value);
          });
        }}
        searchable
        searchPlaceholder="Search admins by name or email..."
        onSearch={(value) => handleFilterChange("search", value)}
        actions={actions}
        bulkActions={bulkActions}
        emptyText="No administrators found"
        emptyIcon={<Shield className="w-12 h-12" />}
      />

      {/* Modals */}
      <AdminFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        admin={selectedAdmin}
        isEditMode={isEditMode}
      />

      <AdminDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        admin={selectedAdmin}
      />
    </div>
  );
};

export default AdminsPage;
